import { useStore } from "react-redux";
import { useAppDispatch } from "./reduxHook";
import { usePatchCaptureMutation } from "../actions/capture";
import { catalogApi } from "../actions/catalogInQuery";
import { upsertCatalogItems } from "../reducers/catalogItemsSlice";
import { CatalogItem, listCatalogRequest } from "../types";
import { usePatchDetectionMutation } from "../actions/detection";
import { RootState } from "../reducers";

type PatchResult =
  ReturnType<typeof catalogApi.util.updateQueryData> extends (
    ...args: any[]
  ) => infer R
    ? R
    : never;

interface UseBookmarkToggleProps {
  catalogItem: CatalogItem;
  queryParams?: listCatalogRequest;
}

export const useBookmarkToggle = ({
  catalogItem,
  queryParams,
}: UseBookmarkToggleProps) => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const [patchDetection, { isLoading: isPatchingDetection }] =
    usePatchDetectionMutation();
  const [patchCapture, { isLoading: isPatchingCapture }] =
    usePatchCaptureMutation();

  const isBookmarking = isPatchingDetection || isPatchingCapture;

  const handleToggleBookmark = async () => {
    const { source, is_bookmarked, id } = catalogItem;
    const newBookmarkState = !is_bookmarked;

    // 1. Optimistic update — specific entry or all cached entries
    let patchResults: PatchResult[] = [];

    if (queryParams) {
      // Known query (catalog page): update just this entry for instant feedback
      patchResults = [
        dispatch(
          catalogApi.util.updateQueryData(
            "listCatalog",
            queryParams,
            (draft) => {
              const item = draft.items.find((i) => i.id === id);
              if (item) item.is_bookmarked = newBookmarkState;
            },
          ),
        ) as unknown as PatchResult,
      ];
    } else {
      // Unknown query (detail page): loop all cached listCatalog entries
      const allCachedArgs = catalogApi.util.selectCachedArgsForQuery(
        store.getState(),
        "listCatalog",
      );
      patchResults = allCachedArgs.map(
        (cachedArgs) =>
          dispatch(
            catalogApi.util.updateQueryData(
              "listCatalog",
              cachedArgs,
              (draft) => {
                const item = draft.items.find((i) => i.id === id);
                if (item) item.is_bookmarked = newBookmarkState;
              },
            ),
          ) as unknown as PatchResult,
      );
    }

    try {
      if (source === "detection") {
        await patchDetection({
          id,
          data: { is_bookmarked: newBookmarkState },
        }).unwrap();
      } else if (source === "capture") {
        await patchCapture({
          id,
          data: { is_bookmarked: newBookmarkState },
        }).unwrap();
      }

      // 2. Update individual items slice for catalog download
      dispatch(
        upsertCatalogItems([
          {
            ...catalogItem,
            is_bookmarked: newBookmarkState,
          } as any,
        ]),
      );
    } catch (error) {
      // Rollback all optimistic updates
      patchResults.forEach((result) => result.undo());
    }
  };

  return {
    isBookmarking,
    handleToggleBookmark,
  };
};
